import { api } from './client'

// ---- Auth ----
export interface AuthStatus {
  setup_complete: boolean
  authenticated: boolean
  username: string | null
  role: string | null
  user_id: number | null
  mask_contact_info: boolean
}

export interface LoginResponse {
  success: boolean
  username?: string
  role?: string
  otp_required?: boolean
  otp_session_id?: string
  message?: string
}

export const authApi = {
  status: () => api.get<AuthStatus>('/auth/status'),
  login: (username: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { username, password }),
  logout: () => api.post<{ success: boolean }>('/auth/logout'),
  setup: (username: string, password: string) =>
    api.post<{ success: boolean; username: string }>('/auth/setup', { username, password }),
  verifyOtp: (otp_session_id: string, otp_code: string) =>
    api.post<LoginResponse>('/auth/verify-otp', { otp_session_id, otp_code }),
  resendOtp: (otp_session_id: string) =>
    api.post<{ success: boolean; otp_session_id: string }>('/auth/resend-otp', { otp_session_id }),
  getBlockedIps: () =>
    api.get<{ success: boolean; data: Array<{ ip_address: string; attempt_count: number; is_blocked: boolean; ttl_seconds: number; block_remaining_minutes: number }>; total: number }>('/auth/blocked-ips'),
  unblockIp: (ip: string) =>
    api.post<{ success: boolean; message: string }>(`/auth/blocked-ips/${encodeURIComponent(ip)}/unblock`),
}

export interface AccessLogEntry {
  id: number
  user_id: number
  username: string
  full_name: string
  action_type: string
  action_entity_id: number | null
  ip_address: string
  country_code: string | null
  country_name: string | null
  region: string | null
  city: string | null
  latitude: number | null
  longitude: number | null
  org: string | null
  user_agent: string | null
  geo_resolved: boolean
  created_at: string
}

export const adminApi = {
  getAccessLog: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: AccessLogEntry[]; total: number }>(`/admin/access-log${qs}`)
  },
}

// ---- Users ----
export interface User {
  id: number
  username: string
  full_name: string | null
  email: string | null
  role: string
  is_active: boolean
  last_login_at: string | null
  created_at: string
  show_full_contact_info?: boolean
  allow_data_transfers?: boolean
  phone_number?: string | null
  otp_enabled?: boolean
}

export const usersApi = {
  me: () => api.get<{ success: boolean; data: User }>('/users/me'),
  list: (includeInactive = false) =>
    api.get<{ users: User[] }>(`/users?include_inactive=${includeInactive}`),
  create: (data: { username: string; password: string; role: string; full_name?: string; email?: string }) =>
    api.post<{ success: boolean; data: User }>('/users', data),
  update: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: User }>(`/users/${id}`, data),
  deactivate: (id: number) =>
    api.post<{ success: boolean }>(`/users/${id}/deactivate`),
  resetPassword: (id: number, data: { new_password: string }) =>
    api.post<{ success: boolean; message: string }>(`/users/${id}/reset-password`, data),
  provisionPlivoEndpoint: (id: number) =>
    api.post<{ success: boolean; message: string; data: { username: string; already_exists: boolean } }>(`/users/${id}/provision-plivo-endpoint`),
  provisionAllPlivoEndpoints: () =>
    api.post<{ success: boolean; data: { provisioned: number; failed: number; details: any[] } }>('/users/provision-all-plivo-endpoints'),
  updateAvailability: (userId: number, data: { is_available: boolean }) =>
    api.patch<{ success: boolean }>(`/users/${userId}/availability`, data),
}

// ---- Assignment ----
export const assignmentApi = {
  // Rules
  listRules: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/assignment/rules'),
  getDefaults: () =>
    api.get<{ success: boolean; data: { name: string; strategy: string; exists: boolean }[] }>('/assignment/defaults'),
  seedDefaults: (names?: string[]) =>
    api.post<{ success: boolean; created: string[]; skipped: string[]; message: string }>('/assignment/seed-defaults', names ? { names } : {}),
  createRule: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>('/assignment/rules', data),
  updateRule: (ruleId: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: Record<string, unknown> }>(`/assignment/rules/${ruleId}`, data),
  deleteRule: (ruleId: number) =>
    api.delete<{ success: boolean }>(`/assignment/rules/${ruleId}`),

  // Queue & Actions
  getQueue: (params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams()
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    const q = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ success: boolean; leads: Record<string, unknown>[]; total: number }>(`/assignment/queue${q}`)
  },
  autoAssign: (data?: { lead_ids?: number[] }) =>
    api.post<{ success: boolean; data: { assigned: number; skipped: number; errors: string[] } }>('/assignment/auto-assign', data || {}),

  // Workload
  getWorkload: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/assignment/workload'),

  // SLA
  checkSla: () =>
    api.get<{ success: boolean; data: Record<string, unknown> }>('/assignment/sla/check'),
  getSlaStatus: () =>
    api.get<{ success: boolean; data: Record<string, unknown> }>('/assignment/sla/dashboard'),
}

// ---- Companies ----
export interface Company {
  id: number
  name: string
  website: string | null
  industry: string | null
  business_type: string | null
  size: string | null
  city: string | null
  state: string | null
  phone: string | null
  lead_count: number
  notes: string | null
  address: string | null
  created_at: string
  updated_at: string
}

export interface CompanyDetail extends Company {
  leads: Lead[]
  pipeline_summary: Record<string, number>
  avg_score: number | null
}

export const companiesApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ companies: Company[]; total: number }>(`/companies${qs}`)
  },
  get: (id: number) => api.get<{ success: boolean; data: CompanyDetail }>(`/companies/${id}`),
  create: (data: Record<string, unknown>) => api.post<{ success: boolean; data: Company }>('/companies', data),
  update: (id: number, data: Record<string, unknown>) => api.patch<{ success: boolean; data: Company }>(`/companies/${id}`, data),
  delete: (id: number) => api.delete<{ success: boolean }>(`/companies/${id}`),
  search: (q: string) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/companies/search?q=${encodeURIComponent(q)}`),
  setPrimaryContact: (companyId: number, data: { lead_id: number }) =>
    api.put<{ success: boolean }>(`/companies/${companyId}/primary-contact`, data),
  setSecondaryContact: (companyId: number, data: { lead_id: number }) =>
    api.put<{ success: boolean }>(`/companies/${companyId}/secondary-contact`, data),
}

// ---- Leads ----
export interface Lead {
  id: number
  name: string
  email: string | null
  phone_number: string | null
  company: string | null
  company_id: number | null
  title: string | null
  industry: string | null
  stage: string
  score: number | null
  quality_segment: string | null
  deal_value: number | null
  owner_id: number | null
  created_at: string
  [key: string]: unknown
}

export interface ImportPreviewRow {
  row_number: number
  status: 'valid' | 'invalid' | 'duplicate' | 'skipped'
  cleaned_data: Record<string, unknown>
  errors: Array<{ field: string; message: string }>
  duplicate_info: { lead_id: string | null; name: string; match_field: string; row_number: number | null } | null
}

export interface ImportPreview {
  total_rows: number
  valid_count: number
  invalid_count: number
  duplicate_count: number
  skipped_count: number
  rows: ImportPreviewRow[]
}

export const leadsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ leads: Lead[]; total: number }>(`/leads${qs}`)
  },
  get: (id: string | number) => api.get<{ success: boolean; data: Lead }>(`/leads/${id}`),
  create: (data: Record<string, unknown>) => api.post<{ success: boolean; data: Lead }>('/leads', data),
  update: (id: string | number, data: Record<string, unknown>) => api.patch<{ success: boolean; data: Lead }>(`/leads/${id}`, data),
  delete: (id: string | number) => api.delete<{ success: boolean }>(`/leads/${id}`),
  updateStage: (id: string | number, stage: string) => api.patch<{ success: boolean; data: Lead }>(`/leads/${id}/stage`, { stage }),
  reminders: () => api.get<{ due_today: Lead[]; overdue_count: number }>('/leads/reminders'),
  assignLead: (leadId: string | number, data: { assigned_to: number; reason?: string }) =>
    api.post<{ success: boolean }>(`/leads/${leadId}/assign`, data),
  getAssignments: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/assignments`),
  getSubmissions: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/submissions`),
  getNotes: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/notes`),
  addNote: (leadId: string | number, data: { content: string }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/leads/${leadId}/notes`, data),
  getEmails: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/emails`),
  getPhones: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/phones`),
  setPrimaryEmail: (leadId: string | number, data: { email_id: number }) =>
    api.put<{ success: boolean }>(`/leads/${leadId}/primary-email`, data),
  setPrimaryPhone: (leadId: string | number, data: { phone_id: number }) =>
    api.put<{ success: boolean }>(`/leads/${leadId}/primary-phone`, data),
  getDuplicates: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/leads/duplicates'),
  mergeLead: (leadId: string | number, data: { merge_lead_id: number }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/leads/${leadId}/merge`, data),
  unmergeLead: (leadId: string | number, data: { snapshot_id: number }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/leads/${leadId}/unmerge`, data),
  getMergeHistory: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/merge-history`),
  getEditHistory: (leadId: string | number) =>
    api.get<{ success: boolean; data: Array<{ id: number; lead_id: number; edited_by: number; edited_by_name: string | null; edited_at: string; changes: Record<string, { old: unknown; new: unknown }> }> }>(`/leads/${leadId}/edit-history`),

  // CSV Import/Export
  downloadTemplate: () => window.open('/api/leads/template-csv', '_blank'),
  previewImportCsv: (file: File, columnMapping: Record<string, string>) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('column_mapping', JSON.stringify(columnMapping))
    return api.upload<{ success: boolean; data: ImportPreview }>('/leads/import-csv/preview', formData)
  },
  importCsv: (file: File, columnMapping: Record<string, string>) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('column_mapping', JSON.stringify(columnMapping))
    return api.upload<{ success: boolean; data: { created: number; duplicates: number; errors: string[] } }>('/leads/import-csv', formData)
  },
  exportCsv: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    window.open(`/api/leads/export-csv${qs}`, '_blank')
  },
  bulkUpdate: (data: { lead_ids: number[]; lead_source?: string; source?: string; channel?: string; stage?: string; meta_campaign_id?: string }) =>
    api.post<{ success: boolean; data: { updated: number; not_found: number; total_requested: number } }>('/leads/bulk-update', data),
}

// ---- Social Analytics ----
export interface SocialConnection {
  id: number
  platform: string
  account_id: string
  account_name: string | null
  account_url: string | null
  status: string
  sync_interval_minutes: number
  last_synced_at: string | null
  last_error: string | null
  token_expiry: string | null
  connected_by: number | null
  created_at: string | null
}

export interface SocialMetric {
  id: number
  date: string
  followers: number | null
  followers_delta: number | null
  impressions: number | null
  reach: number | null
  engagements: number | null
  profile_views: number | null
  website_clicks: number | null
}

export interface SocialPost {
  id: number
  platform_post_id: string
  post_type: string | null
  title: string | null
  text: string | null
  permalink: string | null
  media_url: string | null
  thumbnail_url: string | null
  published_at: string | null
  impressions: number | null
  reach: number | null
  likes: number | null
  comments: number | null
  shares: number | null
  saves: number | null
  clicks: number | null
  video_views: number | null
  engagement_rate: number | null
  total_engagements: number
}

export const socialApi = {
  // Connections
  listConnections: (platform?: string) => {
    const qs = platform ? `?platform=${platform}` : ''
    return api.get<{ success: boolean; data: SocialConnection[] }>(`/social/connections${qs}`)
  },
  createConnection: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: SocialConnection }>('/social/connections', data),
  deleteConnection: (id: number) =>
    api.delete<{ success: boolean }>(`/social/connections/${id}`),
  syncConnection: (id: number) =>
    api.post<{ success: boolean; data?: Record<string, unknown>; task_id?: string }>(`/social/connections/${id}/sync`),

  // Instagram
  discoverInstagram: () =>
    api.get<{ success: boolean; data: Array<{ ig_user_id: string; username: string; name: string; followers_count: number }> }>('/social/instagram/accounts'),
  connectInstagram: () =>
    api.post<{ success: boolean; data: SocialConnection; message: string }>('/social/instagram/connect'),

  // Metrics
  getMetrics: (connectionId: number, days = 30) =>
    api.get<{ success: boolean; data: SocialMetric[] }>(`/social/connections/${connectionId}/metrics?days=${days}`),
  getSummary: (connectionId: number, days = 7) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/social/connections/${connectionId}/summary?days=${days}`),

  // Posts
  getPosts: (connectionId: number, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: SocialPost[]; total: number }>(`/social/connections/${connectionId}/posts${qs}`)
  },
  getTopPosts: (connectionId: number, metric = 'engagements', limit = 10, days?: number) => {
    let qs = `?metric=${metric}&limit=${limit}`
    if (days) qs += `&days=${days}`
    return api.get<{ success: boolean; data: SocialPost[] }>(`/social/connections/${connectionId}/top-posts${qs}`)
  },
  getBottomPosts: (connectionId: number, metric = 'engagement_rate', limit = 10) =>
    api.get<{ success: boolean; data: SocialPost[] }>(`/social/connections/${connectionId}/bottom-posts?metric=${metric}&limit=${limit}`),

  // Demographics
  getDemographics: (connectionId: number) =>
    api.get<{ success: boolean; data: { available: boolean; gender?: any[]; age_groups?: any[]; top_cities?: any[]; top_countries?: any[] } }>(`/social/connections/${connectionId}/demographics`),

  // Analytics
  getPostTypeBreakdown: (connectionId: number, days?: number) => {
    const qs = days ? `?days=${days}` : ''
    return api.get<{ success: boolean; data: Array<{ post_type: string; count: number; avg_likes: number; avg_comments: number; avg_engagement_rate: number; total_engagements: number; avg_reach: number }> }>(`/social/connections/${connectionId}/post-type-breakdown${qs}`)
  },
  getBestPostingTimes: (connectionId: number, days = 90) =>
    api.get<{ success: boolean; data: { by_day: any[]; by_hour: any[]; best_day: string | null; best_hour: string | null; total_posts_analyzed: number } }>(`/social/connections/${connectionId}/best-posting-times?days=${days}`),
  getHashtagPerformance: (connectionId: number, days?: number) => {
    const qs = days ? `?days=${days}` : ''
    return api.get<{ success: boolean; data: Array<{ hashtag: string; posts_count: number; avg_engagements: number; total_engagements: number }> }>(`/social/connections/${connectionId}/hashtag-performance${qs}`)
  },

  // Dashboard & Report
  getDashboard: (days = 7) =>
    api.get<{ success: boolean; data: { period_days: number; platforms: any[]; totals: Record<string, number> } }>(`/social/dashboard?days=${days}`),
  getWeeklyReport: () =>
    api.get<{ success: boolean; data: Record<string, any> }>('/social/report/weekly'),
}

// ---- Tasks ----
export interface Task {
  id: number
  title: string
  description: string | null
  task_type: string
  status: string
  priority: string
  due_date: string | null
  completed_at: string | null
  lead_id: number | null
  company_id: number | null
  assigned_to: number
  assigned_user_name: string | null
  created_by: number
  is_recurring: boolean
  recurrence_rule: string | null
  lead_name: string | null
  company_name: string | null
  created_at: string
}

export const tasksApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ tasks: Task[]; total: number }>(`/tasks${qs}`)
  },
  get: (id: number) => api.get<{ success: boolean; data: Task }>(`/tasks/${id}`),
  create: (data: Record<string, unknown>) => api.post<{ success: boolean; data: Task }>('/tasks', data),
  update: (id: number, data: Record<string, unknown>) => api.patch<{ success: boolean; data: Task }>(`/tasks/${id}`, data),
  complete: (id: number) => api.post<{ success: boolean; data: Task }>(`/tasks/${id}/complete`),
  cancel: (id: number) => api.post<{ success: boolean; data: Task }>(`/tasks/${id}/cancel`),
  delete: (id: number) => api.delete<{ success: boolean }>(`/tasks/${id}`),
  overdue: () => api.get<{ tasks: Task[]; total: number }>('/tasks/overdue'),
  getNotes: (taskId: number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/tasks/${taskId}/notes`),
  addNote: (taskId: number, data: { content: string }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/tasks/${taskId}/notes`, data),
}

// ---- Notifications ----
export interface Notification {
  id: number
  title: string
  body: string | null
  notification_type: string
  entity_type: string | null
  entity_id: number | null
  is_read: boolean
  created_at: string
}

export const notificationsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ notifications: Notification[]; total: number }>(`/notifications${qs}`)
  },
  count: () => api.get<{ unread_count: number }>('/notifications/count'),
  markRead: (id: number) => api.post<{ success: boolean }>(`/notifications/${id}/read`),
  markAllRead: () => api.post<{ success: boolean; count: number }>('/notifications/read-all'),
  poll: (params: { since_id: number; types?: string[] }) => {
    const qs = new URLSearchParams({ since_id: String(params.since_id) })
    params.types?.forEach((t) => qs.append('types', t))
    return api.get<{ success: boolean; notifications: Record<string, unknown>[]; max_id: number }>(
      `/notifications/poll?${qs.toString()}`,
    )
  },
}

// ---- Reports ----
export const reportsApi = {
  dashboard: (period = 'last_30_days') =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/reports/dashboard?period=${period}`),
  pipeline: (period = 'last_30_days') =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/reports/pipeline?period=${period}`),
  activity: (period = 'last_30_days', repId?: number) => {
    const qs = repId ? `&rep_id=${repId}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/reports/activity?period=${period}${qs}`)
  },
  leadSources: () => api.get<{ success: boolean; data: Record<string, unknown> }>('/reports/lead-sources'),
  scoreDistribution: () => api.get<{ success: boolean; data: Record<string, unknown> }>('/reports/score-distribution'),
  revenue: (period = 'this_year') =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/reports/revenue?period=${period}`),
  voipCosts: (period = 'this_month', groupBy = 'day', teamId?: number, userId?: number) => {
    const params = new URLSearchParams({ period, group_by: groupBy })
    if (teamId != null) params.set('team_id', String(teamId))
    if (userId != null) params.set('user_id', String(userId))
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/reports/voip-costs?${params}`)
  },
  backfillVoipCosts: () =>
    api.post<{ success: boolean; data: { total_calls: number; captured: number; pending: number; failed: number } }>('/reports/voip-costs/backfill'),
}

// ---- Email ----
export interface EmailTemplate {
  id: number
  name: string
  subject: string
  body_html: string
  body_text: string | null
  category: string | null
  is_active: boolean
  created_at: string
}

export interface SentEmail {
  id: number
  lead_id: number | null
  subject: string
  to_address: string
  status: string
  open_count: number
  click_count: number
  sent_at: string | null
  created_at: string
}

export const emailApi = {
  templates: () => api.get<{ templates: EmailTemplate[] }>('/email/templates'),
  createTemplate: (data: Record<string, unknown>) => api.post<{ success: boolean; data: EmailTemplate }>('/email/templates', data),
  updateTemplate: (id: number, data: Record<string, unknown>) => api.patch<{ success: boolean; data: EmailTemplate }>(`/email/templates/${id}`, data),
  deleteTemplate: (id: number) => api.delete<{ success: boolean }>(`/email/templates/${id}`),
  send: (data: { lead_id: number; template_id?: number; subject?: string; html_body?: string }) =>
    api.post<{ success: boolean; data: SentEmail }>('/email/send', data),
  leadEmails: (leadId: string | number) => api.get<{ emails: SentEmail[]; total: number }>(`/leads/${leadId}/emails`),
  preview: (data: { lead_id: number; template_id: number }) =>
    api.post<{ success: boolean; data: { subject: string; body_html: string } }>('/email/preview', data),
}

// ---- Meta Ads ----
export const metaApi = {
  // Connections (list all)
  listConnections: () =>
    api.get<{ success: boolean; data: Array<{ id: number; label: string | null; brand: string | null; ad_account_id: string | null; page_name: string | null; status: string }> }>('/meta/connections'),

  // Connection
  getConnection: () =>
    api.get<{ success: boolean; data: Record<string, unknown> | null }>('/meta/connection'),
  createConnection: (data: { app_id: string; app_secret: string }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>('/meta/connection', data),
  deleteConnection: () =>
    api.delete<{ success: boolean; message: string }>('/meta/connection'),
  testConnection: () =>
    api.get<{ success: boolean; data: { valid: boolean; scopes: string[]; expires_at: number | null } }>('/meta/connection/test'),

  // OAuth
  getOAuthUrl: () =>
    api.get<{ success: boolean; data: { url: string; state: string } }>('/meta/oauth/url'),
  exchangeCode: (data: { code: string; redirect_uri: string }) =>
    api.post<{ success: boolean; data: { status: string; expires_in: number | null } }>('/meta/oauth/callback', data),

  // Account / Page
  listAdAccounts: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/meta/accounts'),
  listPages: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/meta/pages'),
  selectAccountAndPage: (data: { ad_account_id: string; ad_account_name: string; page_id: string; page_name: string }) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>('/meta/connection/select', data),

  // Forms
  listForms: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/meta/forms'),
  getFormQuestions: (formId: string) =>
    api.get<{ success: boolean; data: { questions: Record<string, unknown>[]; suggested_mapping: Record<string, string> } }>(`/meta/forms/${formId}/questions`),
  saveFormMapping: (formId: string, data: { field_mapping: Record<string, string>; auto_sync_enabled: boolean }) =>
    api.put<{ success: boolean; data: Record<string, unknown> }>(`/meta/forms/${formId}/mapping`, data),

  // Lead Preview
  previewFormLeads: (formId: string, limit = 25) =>
    api.get<{ success: boolean; data: { form_id: string; form_name: string; total_fetched: number; has_mapping: boolean; leads: Array<{ meta_lead_id: string; created_time: string; raw_fields: Record<string, string>; mapped_fields: Record<string, string>; ad_id: string | null; campaign_id: string | null }> } }>(`/meta/forms/${formId}/preview-leads?limit=${limit}`),

  // Lead Sync
  syncAllLeads: () =>
    api.post<{ success: boolean; data: { forms_synced: number; total_new_leads: number; results: Record<string, unknown>[] } }>('/meta/sync/leads'),
  syncFormLeads: (formId: string) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/meta/sync/leads/${formId}`),
  getSyncStatus: () =>
    api.get<{ success: boolean; data: { sync_interval_minutes: number | null; forms: Record<string, unknown>[] } }>('/meta/sync/status'),
  setSyncSchedule: (data: { sync_interval_minutes: number | null; enabled: boolean }) =>
    api.put<{ success: boolean; data: { interval_minutes: number | null; enabled: boolean } }>('/meta/sync/schedule', data),

  // Ads
  syncAds: () =>
    api.post<{ success: boolean; data: { campaigns_synced: number; insights_synced: number } }>('/meta/sync/ads'),
  listCampaigns: () =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>('/meta/campaigns'),
  getCampaignInsights: (campaignId: string, dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/campaigns/${campaignId}/insights${qs}`)
  },
  listAdSets: (campaignId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/campaigns/${campaignId}/adsets`),
  listAds: (adsetId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/adsets/${adsetId}/ads`),
  getAdSetInsights: (adsetId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/adsets/${adsetId}/insights`),
  getAdInsights: (adId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/ads/${adId}/insights`),
  toggleAdsTracking: (data: { enabled: boolean }) =>
    api.put<{ success: boolean; data: { ads_tracking_enabled: boolean } }>('/meta/connection/ads-tracking', data),
}

// ---- Meta Analytics ----
export const metaAnalyticsApi = {
  getOverview: (dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/meta/analytics/overview${qs}`)
  },
  getCampaigns: (dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/analytics/campaigns${qs}`)
  },
  getTrends: (dateFrom?: string, dateTo?: string, campaignId?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (campaignId) params.set('campaign_id', campaignId)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/analytics/trends${qs}`)
  },
  recompute: (connectionId?: number) => {
    const params = new URLSearchParams()
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.post<{ success: boolean; data: Record<string, unknown> }>(`/meta/analytics/recompute${qs}`)
  },
  getLeadQuality: (dateFrom?: string, dateTo?: string, groupBy?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (groupBy) params.set('group_by', groupBy)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/analytics/lead-quality${qs}`)
  },
  getCohorts: (dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/analytics/cohorts${qs}`)
  },
  getAudienceSegments: (dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/meta/analytics/audience-segments${qs}`)
  },
  getSalesEffectiveness: (dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/meta/analytics/sales-effectiveness${qs}`)
  },
  getCampaignFunnel: (campaignId: string, dateFrom?: string, dateTo?: string, connectionId?: number) => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    if (connectionId) params.set('connection_id', String(connectionId))
    const qs = params.toString() ? `?${params}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/meta/analytics/campaigns/${campaignId}/funnel${qs}`)
  },
}

// ---- VoIP ----
export interface VoIPCall {
  id: number
  lead_id: number | null
  provider: string
  direction: string
  from_number: string
  to_number: string
  status: string
  call_status: string
  duration_seconds: number | null
  recording_url: string | null
  recording_storage_path: string | null
  lead_name: string | null
  created_at: string
  call_outcome: string | null
  call_quality_score: number | null
  outcome_notes: string | null
  notes: string | null
  dial_number?: string
}

export const voipApi = {
  token: () => api.get<{ success: boolean; data: { token: string; endpoint_name: string; provider: string } }>('/voip/token'),
  call: (data: { lead_id: number; to_number: string }) => api.post<{ success: boolean; data: VoIPCall }>('/voip/call', data),
  endCall: (id: number) => api.post<{ success: boolean }>(`/voip/call/${id}/end`),
  status: (id: number) => api.get<{ success: boolean; data: VoIPCall }>(`/voip/call/${id}/status`),
  updateStatus: (id: number, data: { status: string; duration_seconds?: number }) =>
    api.patch<{ success: boolean; data: VoIPCall }>(`/voip/call/${id}/status`, data),
  leadCalls: (leadId: string | number) => api.get<{ calls: VoIPCall[]; total: number }>(`/voip/leads/${leadId}/calls`),
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ calls: VoIPCall[]; total: number }>(`/voip/calls${qs}`)
  },
  rateCall: (id: number, data: { call_outcome?: string; call_quality_score?: number; outcome_notes?: string }) =>
    api.patch<{ success: boolean; data: VoIPCall }>(`/voip/call/${id}/rate`, data),
  updateNotes: (id: number, notes: string) =>
    api.patch<{ success: boolean }>(`/voip/call/${id}/notes`, { notes }),
  saveNotesToLead: (id: number) =>
    api.post<{ success: boolean; saved: boolean; note?: Record<string, unknown> }>(`/voip/call/${id}/notes/save-to-lead`),
  createLeadFromCall: (callId: number, data: { name: string; company?: string; email?: string }) =>
    api.post<{ success: boolean; lead_id: number; lead_public_id: string; lead_name: string; call_log_id: number; is_duplicate: boolean; linked_count: number }>(
      `/voip/call/${callId}/create-lead`, data,
    ),
  unknownCallers: () =>
    api.get<{ success: boolean; data: Array<{ phone_number: string; call_count: number; connected_count: number; missed_count: number; first_call_at: string | null; last_call_at: string | null }>; total: number }>(
      '/voip/unknown-callers',
    ),
  callerInfo: (phone: string) =>
    api.get<{ success: boolean; lead_name: string | null; lead_id: number | null; display_number: string }>(
      `/voip/caller-info?phone=${encodeURIComponent(phone)}`,
    ),
  exportCsv: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    window.open(`/api/voip/calls/export-csv${qs}`, '_blank')
  },
  backfillRecordings: (data: { days_back?: number; dry_run?: boolean }) =>
    api.post<{ success: boolean; data: { checked: number; found: number; downloaded: number; already_have: number; failed: number } }>('/voip/backfill-recordings', data),
  backfillBilling: (data: { days_back?: number }) =>
    api.post<{ success: boolean; data: { total_checked: number; synced: number; skipped: number; failed: number } }>('/voip/backfill-billing', data),
}

// ---- CallPulse Playbooks ----
export const playbookApi = {
  list: () => api.get<{ success: boolean; data: Record<string, unknown>[] }>('/playbooks'),
  getDefaults: () =>
    api.get<{ success: boolean; data: { name: string; exists: boolean }[] }>('/playbooks/defaults'),
  seedDefaults: (names?: string[]) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>('/playbooks/seed-defaults', names ? { names } : {}),
  create: (data: Record<string, unknown>) => api.post<{ success: boolean; data: Record<string, unknown> }>('/playbooks', data),
  get: (id: number) => api.get<{ success: boolean; data: Record<string, unknown> }>(`/playbooks/${id}`),
  update: (id: number, data: Record<string, unknown>) => api.put<{ success: boolean; data: Record<string, unknown> }>(`/playbooks/${id}`, data),
  delete: (id: number) => api.delete<{ success: boolean }>(`/playbooks/${id}`),
  match: (leadId: string | number, callNumber?: number) => {
    const qs = callNumber ? `?call_number=${callNumber}` : ''
    return api.get<{ success: boolean; data: Record<string, unknown> | null }>(`/leads/${leadId}/playbook-match${qs}`)
  },
  createResponseSet: (leadId: string | number, data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/leads/${leadId}/playbook-responses`, data),
  saveResponses: (responseSetId: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: Record<string, unknown> }>(`/playbook-response-sets/${responseSetId}`, data),
  submitResponseSet: (responseSetId: number, data?: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/playbook-response-sets/${responseSetId}/submit`, data || {}),
  listResponses: (leadId: string | number) =>
    api.get<{ success: boolean; data: Record<string, unknown>[] }>(`/leads/${leadId}/playbook-responses`),
  getResponseSet: (responseSetId: number) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/playbook-response-sets/${responseSetId}`),
  deleteResponseSet: (responseSetId: number) =>
    api.delete<{ success: boolean }>(`/playbook-response-sets/${responseSetId}`),
}

// ---- Jobs / Task Monitor ----
export interface WorkerInfo {
  name: string
  status: string
  active_tasks: number
  reserved_tasks: number
  registered_tasks: string[]
  concurrency: number
  completed: number
  pid: number | null
  pool_type: string
  total_by_task: Record<string, number>
}

export interface ActiveTask {
  id: string
  name: string
  full_name: string
  worker: string
  started: number | null
}

export interface WorkerStats {
  success: boolean
  workers: WorkerInfo[]
  active_tasks: ActiveTask[]
  reserved_tasks: ActiveTask[]
  summary: {
    workers_online: number
    active: number
    reserved: number
    completed: number
  }
}

export interface QueueInfo {
  name: string
  messages: number
  consumers: number
}

export interface JobStatus {
  success: boolean
  task_id: string
  status: string
  result?: Record<string, unknown>
  error?: string
  traceback?: string
}

export const jobsApi = {
  workerStats: () => api.get<WorkerStats>('/jobs/workers/stats'),
  queueLengths: () => api.get<{ success: boolean; queues: QueueInfo[] }>('/jobs/workers/queue-lengths'),
  getJob: (taskId: string) => api.get<JobStatus>(`/jobs/${taskId}`),
  revokeJob: (taskId: string) => api.delete<{ success: boolean }>(`/jobs/${taskId}`),
  enqueue: (taskName: string, args: unknown[] = [], kwargs: Record<string, unknown> = {}) =>
    api.post<{ success: boolean; task_id: string }>('/jobs/enqueue', { task_name: taskName, args, kwargs }),
}

// ---- Teams ----
export interface Team {
  id: number
  name: string
  description: string | null
  team_type: string
  is_active: boolean
  routing_priority: number
  max_concurrent_calls: number
  routing_strategy: string
  routing_config: Record<string, unknown> | null
  fallback_team_id: number | null
  fallback_team_name: string | null
  fallback_action: string | null
  member_count: number
  online_count: number
  created_at: string
}

export interface TeamMember {
  id: number
  team_id: number
  user_id: number
  user_name: string
  user_role: string
  team_role: string
  is_active: boolean
  availability: string
  max_concurrent_calls: number
  active_call_count: number
  skills: string[]
  last_call_assigned_at: string | null
}

export interface IVRMenu {
  id: number
  name: string
  phone_number: string | null
  welcome_message: string
  has_welcome_audio?: boolean
  invalid_input_message: string | null
  timeout_message: string | null
  timeout_seconds: number
  max_retries: number
  fallback_action: string
  fallback_team_id: number | null
  is_active: boolean
  options: IVROption[]
}

export interface IVROption {
  id: number
  menu_id: number
  digit: string
  label: string
  action: string
  target_team_id: number | null
  target_team_name: string | null
  target_user_id: number | null
  target_user_name: string | null
  target_menu_id: number | null
  transfer_number: string | null
  is_active: boolean
}

export const teamsApi = {
  // Teams CRUD
  list: (teamType?: string) =>
    api.get<{ success: boolean; teams: Team[] }>(`/teams${teamType ? `?team_type=${teamType}` : ''}`),
  get: (id: number) =>
    api.get<{ success: boolean; data: Team & { members: TeamMember[] } }>(`/teams/${id}`),
  create: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Team }>('/teams', data),
  update: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: Team }>(`/teams/${id}`, data),
  delete: (id: number) =>
    api.delete<{ success: boolean }>(`/teams/${id}`),

  // Members
  addMember: (teamId: number, data: { user_id: number; team_role?: string; skills?: string[] }) =>
    api.post<{ success: boolean; data: TeamMember }>(`/teams/${teamId}/members`, data),
  removeMember: (teamId: number, userId: number) =>
    api.delete<{ success: boolean }>(`/teams/${teamId}/members/${userId}`),
  updateMember: (teamId: number, userId: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: TeamMember }>(`/teams/${teamId}/members/${userId}`, data),

  // Availability
  setAvailability: (teamId: number, availability: string) =>
    api.patch<{ success: boolean }>(`/teams/${teamId}/availability`, { availability }),
  getAvailability: (teamId: number) =>
    api.get<{ success: boolean; members: TeamMember[] }>(`/teams/${teamId}/availability`),

  // Seed defaults (teams only)
  seedDefaults: () =>
    api.post<{ success: boolean; created: string[]; skipped: string[]; message: string }>('/teams/seed-defaults'),

  // History
  configHistory: (teamId: number) =>
    api.get<{ success: boolean; history: Record<string, unknown>[] }>(`/teams/${teamId}/history/config`),
  memberHistory: (teamId: number) =>
    api.get<{ success: boolean; history: Record<string, unknown>[] }>(`/teams/${teamId}/history/members`),
}

// ---- Teams Analytics ----
export interface TeamCallVolume {
  total: number
  inbound: number
  outbound: number
  outcomes: Record<string, number>
  interaction_rate: number
  reach_rate: number
}

export interface TeamPerformance {
  avg_handle_time_seconds: number
  first_call_resolution_rate: number
  connect_rate: number
}

export interface TeamQuality {
  avg_score: number
  total_rated: number
  outcome_distribution: Record<string, number>
}

export interface TeamRouting {
  avg_queue_wait_seconds: number
  avg_agents_tried: number
  strategy_breakdown: Record<string, number>
}

export interface TeamCapacity {
  total_members: number
  online_members: number
  avg_active_calls: number
}

export interface TrendPoint {
  date: string
  calls: number
  avg_duration_seconds: number
}

export interface TeamAnalytics {
  team_id: number
  team_name: string
  team_type: string
  period: string
  call_volume: TeamCallVolume
  performance: TeamPerformance
  quality: TeamQuality
  routing: TeamRouting
  capacity: TeamCapacity
  trends: TrendPoint[]
}

export interface AgentPerformance {
  total_calls: number
  inbound: number
  outbound: number
  outcomes: Record<string, number>
  interaction_rate: number
  reach_rate: number
  avg_duration_minutes: number | null
}

export interface AgentQualityStats {
  avg_score: number
  total_rated: number
  avg_handle_time_seconds?: number | null
}

export interface AgentResponsiveness {
  avg_ring_time_seconds: number | null
  pickup_rate: number
}

export interface AgentVsTeam {
  interaction_rate_delta: number | null
  reach_rate_delta: number | null
  quality_delta?: number | null
}

export interface AgentAnalytics {
  user_id: number
  user_name: string
  team_role: string
  period: string
  performance: AgentPerformance
  quality: AgentQualityStats
  responsiveness: AgentResponsiveness
  vs_team: AgentVsTeam
}

export interface CrossTeamRow {
  team_id: number
  team_name: string
  team_type: string
  total_calls: number
  avg_handle_time_seconds: number
  avg_quality: number
  connect_rate: number
  missed_rate: number
  online_members: number
  total_members: number
  outcomes: Record<string, number>
}

export interface CrossTeamAnalytics {
  period: string
  teams: CrossTeamRow[]
}

export const teamsAnalyticsApi = {
  teamAnalytics: (teamId: number, period = '7d') =>
    api.get<{ success: boolean; data: TeamAnalytics }>(`/teams/${teamId}/analytics?period=${period}`),
  agentAnalytics: (teamId: number, userId: number, period = '7d') =>
    api.get<{ success: boolean; data: AgentAnalytics }>(`/teams/${teamId}/members/${userId}/analytics?period=${period}`),
  crossTeam: (period = '30d') =>
    api.get<{ success: boolean; data: CrossTeamAnalytics }>(`/analytics/teams?period=${period}`),
}

export const ivrApi = {
  seedDefaults: () =>
    api.post<{ success: boolean; created: string[]; skipped: string[]; message: string }>('/ivr/menus/seed-defaults'),
  listMenus: () =>
    api.get<{ success: boolean; menus: IVRMenu[] }>('/ivr/menus'),
  getMenu: (id: number) =>
    api.get<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${id}`),
  createMenu: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: IVRMenu }>('/ivr/menus', data),
  updateMenu: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${id}`, data),
  deleteMenu: (id: number) =>
    api.delete<{ success: boolean }>(`/ivr/menus/${id}`),
  addOption: (menuId: number, data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${menuId}/options`, data),
  updateOption: (menuId: number, digit: string, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${menuId}/options/${digit}`, data),
  deleteOption: (menuId: number, digit: string) =>
    api.delete<{ success: boolean }>(`/ivr/menus/${menuId}/options/${digit}`),
  uploadGreeting: (menuId: number, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.upload<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${menuId}/greeting-audio`, fd)
  },
  deleteGreeting: (menuId: number) =>
    api.delete<{ success: boolean; data: IVRMenu }>(`/ivr/menus/${menuId}/greeting-audio`),
}

// ---- Presence ----
export const presenceApi = {
  heartbeat: (data: { device_type: string; is_active: boolean; tab_id: string }) =>
    api.post<{ success: boolean }>('/presence/heartbeat', data),
  getLive: () =>
    api.get<{ success: boolean; data: { online: Array<{ user_id: number; username: string; full_name: string; role: string; status: string; device_type: string; last_seen_at: string; last_action: string | null; last_action_at: string | null }>; count: number } }>('/presence/live'),
  getLiveTeam: (teamId: number) =>
    api.get<{ success: boolean; data: { team_id: number; members: Array<Record<string, unknown>>; counts: { active: number; idle: number; offline: number }; total: number } }>(`/presence/live/team/${teamId}`),
  getAgentToday: (userId: number) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/presence/agent/${userId}/today`),
  getDailyReport: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: { summaries: Record<string, unknown>[]; count: number } }>(`/presence/reports/daily${qs}`)
  },
  getWeeklyReport: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: { weeks: Record<string, unknown>[]; count: number } }>(`/presence/reports/weekly${qs}`)
  },
  getHeatmap: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: { cells: Array<{ day_of_week: number; hour: number; active_seconds: number; idle_seconds: number; total_seconds: number; idle_ratio: number }>; count: number } }>(`/presence/reports/heatmap${qs}`)
  },
  getDeviceReport: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: { devices: Record<string, unknown>[]; count: number } }>(`/presence/reports/device${qs}`)
  },
  getCorrelation: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: { users: Array<{ user_id: number; username: string; online_hours: number; active_hours: number; call_count: number; call_duration_minutes: number; calls_per_online_hour: number }>; count: number } }>(`/presence/reports/correlation${qs}`)
  },
}

// ---- Data Transfers ----
export interface DataTransferLog {
  id: number
  transfer_type: 'import' | 'export'
  file_name: string
  file_size_bytes: number | null
  s3_key: string | null
  s3_url: string | null
  record_count: number | null
  created_count: number | null
  duplicate_count: number | null
  error_count: number | null
  filters_json: string | null
  initiated_by_name: string | null
  created_at: string
  download_count: number | null
  max_downloads: number | null
}

// ---- Lead Queue ----
export interface QueueItem {
  lead_id: string
  id: number
  name: string
  company: string | null
  phone_number: string | null
  email: string | null
  quality_segment: string | null
  score: number | null
  stage: string | null
  sla_status: string | null
  sla_deadline: string | null
  next_call_date: string | null
  last_attempt_at: string | null
  call_count: number
  contact_attempts: number
  is_parked: boolean
  parked_at: string | null
  park_reason: string | null
  created_at: string | null
  owner_id: number | null
  reason: string | null
  last_call_date: string | null
  last_call_status: string | null
  last_call_direction: string | null
  last_call_duration: number | null
}

export interface QueueStats {
  up_next: number
  overdue_callbacks: number
  sla_breached: number
  sla_warning: number
  parked: number
  calls_today: number
  connects_today: number
  connect_rate: number
  outcomes_today?: Record<string, number>
}

export const queueApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: QueueItem[]; total: number; tab: string }>(`/queue${qs}`)
  },
  stats: () => api.get<{ success: boolean; data: QueueStats }>('/queue/stats'),
  skip: (leadId: number, hours: number = 4) =>
    api.post<{ success: boolean }>(`/queue/${leadId}/skip`, { hours }),
  snooze: (leadId: number, until: string) =>
    api.post<{ success: boolean }>(`/queue/${leadId}/snooze`, { until }),
  park: (leadId: number) =>
    api.post<{ success: boolean }>(`/queue/${leadId}/park`),
  unpark: (leadId: number) =>
    api.post<{ success: boolean }>(`/queue/${leadId}/unpark`),
}

// ---- Data Transfers ----
export const dataTransferApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: (DataTransferLog & { has_file?: boolean; downloads_remaining?: number })[]; total: number }>(`/data-transfers${qs}`)
  },
  get: (id: number) =>
    api.get<{ success: boolean; data: DataTransferLog & { has_file: boolean; downloads_remaining: number; download_history: Array<{ user_id: number; user_name: string | null; downloaded_at: string }> } }>(`/data-transfers/${id}`),
  downloadUrl: (id: number) => `/api/data-transfers/${id}/download`,
}

// ---- Instagram Seller Discovery ----
export const igDiscoveryApi = {
  // Search queries
  listQueries: () => api.get<{ success: boolean; data: any[]; total: number }>('/ig-discovery/queries'),
  createQuery: (data: { query_text: string; label: string; category?: string; priority?: number }) =>
    api.post<{ success: boolean; data: any }>('/ig-discovery/queries', data),
  updateQuery: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: any }>(`/ig-discovery/queries/${id}`, data),
  deleteQuery: (id: number) => api.delete<{ success: boolean }>(`/ig-discovery/queries/${id}`),
  seedQueries: () => api.post<{ success: boolean; created: number }>('/ig-discovery/queries/seed'),

  // Discovery jobs
  launchJob: (data?: { query_ids?: number[]; category?: string }) =>
    api.post<{ success: boolean; data: any }>('/ig-discovery/search', data || {}),
  listJobs: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: any[]; total: number }>(`/ig-discovery/jobs${qs}`)
  },
  getJob: (id: number) => api.get<{ success: boolean; data: any }>(`/ig-discovery/jobs/${id}`),
  getJobProgress: (id: number) =>
    api.get<{ success: boolean; data: any }>(`/ig-discovery/jobs/${id}`),
  continueJob: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/jobs/${id}/continue`),
  abandonJob: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/jobs/${id}/abandon`),

  // Review
  listReview: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: any[]; total: number }>(`/ig-discovery/review${qs}`)
  },
  reviewProspect: (id: number, data: { action: string; rejection_reason?: string; tags?: string[] }) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/review/${id}`, data),
  bulkReview: (data: { prospect_ids: number[]; action: string; rejection_reason?: string }) =>
    api.post<{ success: boolean; data: any }>('/ig-discovery/review/bulk', data),

  // Prospects
  listProspects: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return api.get<{ success: boolean; data: any[]; total: number }>(`/ig-discovery/prospects${qs}`)
  },
  createProspect: (data: { ig_handle: string; notes?: string }) =>
    api.post<{ success: boolean; data: any }>('/ig-discovery/prospects', data),
  getProspect: (id: number) => api.get<{ success: boolean; data: any }>(`/ig-discovery/prospects/${id}`),
  updateProspect: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: any }>(`/ig-discovery/prospects/${id}`, data),
  deleteProspect: (id: number) => api.delete<{ success: boolean }>(`/ig-discovery/prospects/${id}`),

  // Enrichment
  enrichProspect: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/prospects/${id}/enrich`),

  // Outreach
  logOutreach: (id: number, data: { touchpoint_type: string; subject?: string; body?: string; channel?: string }) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/prospects/${id}/outreach`, data),
  listOutreach: (id: number) =>
    api.get<{ success: boolean; data: any[] }>(`/ig-discovery/prospects/${id}/outreach`),

  // Tags
  listTags: () => api.get<{ success: boolean; data: any[] }>('/ig-discovery/tags'),
  createTag: (data: { name: string; color?: string }) =>
    api.post<{ success: boolean; data: any }>('/ig-discovery/tags', data),
  addTags: (prospectId: number, tags: string[]) =>
    api.post<{ success: boolean }>(`/ig-discovery/prospects/${prospectId}/tags`, { tag_names: tags }),
  removeTag: (prospectId: number, tagId: number) =>
    api.delete<{ success: boolean }>(`/ig-discovery/prospects/${prospectId}/tags/${tagId}`),

  // Convert
  convertToLead: (id: number, data: { name: string; company?: string; lead_source?: string }) =>
    api.post<{ success: boolean; data: any }>(`/ig-discovery/prospects/${id}/convert`, data),

  // Stats
  getStats: () => api.get<{ success: boolean; data: any }>('/ig-discovery/stats'),

  // Analytics
  getTrends: (weeks?: number) =>
    api.get<{ success: boolean; data: any }>(`/ig-discovery/analytics/trends${weeks ? `?weeks=${weeks}` : ''}`),
  getTeamPerformance: () =>
    api.get<{ success: boolean; data: any }>('/ig-discovery/analytics/team-performance'),
  getHashtagAnalytics: () =>
    api.get<{ success: boolean; data: any }>('/ig-discovery/analytics/hashtags'),
}

// ---- Pipeline Stages ----
export const pipelineStagesApi = {
  list: (product?: string) =>
    api.get<{ success: boolean; data: any[] }>(`/settings/pipeline-stages${product ? `?product=${product}` : ''}`),
  create: (data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: any }>('/settings/pipeline-stages', data),
  update: (id: number, data: Record<string, unknown>) =>
    api.patch<{ success: boolean; data: any }>(`/settings/pipeline-stages/${id}`, data),
  deactivate: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/settings/pipeline-stages/${id}/deactivate`),
  reorder: (items: { id: number; position: number }[]) =>
    api.post<{ success: boolean }>('/settings/pipeline-stages/reorder', { stages: items }),
  products: () =>
    api.get<{ success: boolean; data: string[] }>('/settings/pipeline-stages/products'),
}

// ---- Scoring Config ----
export const scoringConfigApi = {
  getActive: (scope?: string) =>
    api.get<{ success: boolean; data: any }>(`/settings/scoring-config${scope ? `?scope=${scope}` : ''}`),
  save: (config: Record<string, unknown>, description: string, reason: string) =>
    api.post<{ success: boolean; data: any }>('/settings/scoring-config', { config, description, reason }),
  listVersions: () =>
    api.get<{ success: boolean; data: any[] }>('/settings/scoring-config/versions'),
  revert: (version: number) =>
    api.post<{ success: boolean; data: any }>(`/settings/scoring-config/revert/${version}`),
  preview: (config: Record<string, unknown>) =>
    api.post<{ success: boolean; data: any }>('/settings/scoring-config/preview', { config }),
  rescoreAll: () =>
    api.post<{ success: boolean; data: any }>('/settings/scoring-config/rescore-all'),
  availableAttributes: () =>
    api.get<{ success: boolean; data: { built_in: any[]; callpulse: any[]; factor_types: any[] } }>('/settings/scoring-config/available-attributes'),
}

// ---- Lead Score History ----
export const leadScoreApi = {
  history: (leadId: number | string) =>
    api.get<{ success: boolean; data: any[] }>(`/leads/${leadId}/score-history`),
}

// ---- Business Types ----
export const businessTypesApi = {
  list: () =>
    api.get<{ success: boolean; data: { value: string; label: string }[] }>('/settings/business-types'),
  save: (types: { value: string; label: string }[]) =>
    api.post<{ success: boolean; data: any }>('/settings/business-types', { types }),
}
